import React, { useEffect, useState } from "react";
import { Input, message } from "antd";
import { Grid, Box } from "@mui/material";
import {
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
} from "@/services/api/company/companyApi";
import { CustomButton, Toast } from "@/components";
import useAuth from "@/hooks/useAuth";
import { SaveChangesBoxStyle } from "@/styles/StyledComponents/globalStyle";
import { uploadToCloudinary } from "@/utils/cloudinaryUpload";

const GeneralSetup = ({ data }) => {
  const [updateCompanyDetails, { isLoading: isUpdating }] =
    useUpdateCompanyMutation();
  const [createCompanyDetails, { isLoading: isCreating }] =
    useCreateCompanyMutation(); // ✅ Add create mutation

  const user = useAuth();
  const isLoading = isUpdating || isCreating; // ✅ Combine loading states

  const [formData, setFormData] = useState({
    companyName: data?.name || "",
    companyAbbreviation: data?.abbreviation || "",
    companyEmailAddress: data?.company_email || "",
    companySupportEmailAddress: data?.support_email || "",
    companySupportLine: data?.support_line || "",
    companyLogo: data?.url || "",
    website: data?.website || "",
  });

  console.log("Company data:", data);
  console.log("Company ID:", data?.id);

  const [logo, setLogo] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);

  useEffect(() => {
    if (formData.companyLogo) {
      setLogo(formData.companyLogo);
    } else {
      setLogo(null);
    }
  }, [formData.companyLogo]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = (file) => {
    setUploadedFile(file);

    if (!file) {
      setLogo(null);
      setFormData((prev) => ({
        ...prev,
        companyLogo: "",
      }));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setLogo(reader.result);
      setFormData((prev) => ({
        ...prev,
        companyLogo: reader.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  const toggleEditMode = async () => {
    if (isEditMode) {
      try {
        let imageUrl = formData.companyLogo;

        // If a new file was uploaded, send it to Cloudinary first
        if (uploadedFile && uploadedFile instanceof File) {
          message.loading("Uploading image to Cloudinary...", 0);
          imageUrl = await uploadToCloudinary(uploadedFile);
          message.destroy();
        }

        const form = {
          name: formData.companyName,
          abbreviation: formData.companyAbbreviation,
          company_email: formData.companyEmailAddress,
          support_email: formData.companySupportEmailAddress,
          support_line: formData.companySupportLine,
          url: imageUrl,
          website: formData.website, // ✅ Don't forget website
        };

        // ✅ Check if ID exists - if yes UPDATE, if no CREATE
        if (data?.id) {
          const response = await updateCompanyDetails({
            id: data.id,
            requestData: form,
          }).unwrap();
          message.success("Company details updated successfully");
        } else {
          const response = await createCompanyDetails(form).unwrap();
          message.success("Company created successfully");
        }
      } catch (error) {
        console.error("Operation error:", error);
        message.destroy();
        const errorMessage =
          error?.data?.message ||
          error.message ||
          "Unable to save company details";
        message.error(errorMessage);
      }
    }
    setIsEditMode((prev) => !prev);
  };

  return (
    <div className="bg-white rounded-[20px]">
      <>
        <div className="p-10">
          <div className="mb-8">
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <p sx={{ fontWeight: 600, marginBottom: "8px" }}>
                  Company Details
                </p>
                <p>
                  This will show on your <br /> company's profile.
                </p>
              </Grid>
              <Grid item xs={12} md={8}>
                <Box sx={{ display: "flex", gap: 5, marginBottom: 4 }}>
                  <Box>
                    <p>Company Logo</p>
                    <Input
                      name="companyLogo"
                      value={formData.companyLogo}
                      style={{ display: "none" }}
                      disabled={!isEditMode}
                    />
                    <Box
                      sx={{
                        width: 120,
                        height: 120,
                        borderRadius: "50%",
                        border: "2px dashed #ccc",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        overflow: "hidden",
                      }}
                    >
                      {logo ? (
                        <img
                          src={logo ?? formData.companyLogo}
                          alt="Company Logo"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div style={{ fontSize: 110, color: "#999" }}>📷</div>
                      )}
                    </Box>
                  </Box>
                  {isEditMode && (
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e.target.files[0])}
                      />
                    </div>
                  )}
                </Box>
                <Box sx={{ marginBottom: 3 }}>
                  <p
                    sx={{ marginBottom: 1, fontSize: "14px", fontWeight: 500 }}
                  >
                    Company Name
                  </p>
                  <Input
                    name="companyName"
                    value={formData.companyName || ""}
                    onChange={handleInputChange}
                    disabled={!isEditMode}
                    placeholder="e.g. Sundry Food Services"
                    style={{
                      height: "40px",
                      fontSize: "14px",
                      padding: "8px 12px",
                    }}
                  />
                </Box>
                <Box sx={{ marginBottom: 3 }}>
                  <p
                    sx={{ marginBottom: 1, fontSize: "14px", fontWeight: 500 }}
                  >
                    Company Abbreviation
                  </p>
                  <Input
                    name="companyAbbreviation"
                    value={formData.companyAbbreviation || ""}
                    onChange={handleInputChange}
                    disabled={!isEditMode}
                    placeholder="e.g. SFS"
                    style={{
                      height: "40px",
                      fontSize: "14px",
                      padding: "8px 12px",
                    }}
                  />
                </Box>
                <Box sx={{ marginBottom: 3 }}>
                  <p
                    sx={{ marginBottom: 1, fontSize: "14px", fontWeight: 500 }}
                  >
                    Company Email Address
                  </p>
                  <Input
                    name="companyEmailAddress"
                    value={formData.companyEmailAddress || ""}
                    onChange={handleInputChange}
                    disabled={!isEditMode}
                    placeholder="e.g. company@example.com"
                    style={{
                      height: "40px",
                      fontSize: "14px",
                      padding: "8px 12px",
                    }}
                  />
                </Box>
                <Box sx={{ marginBottom: 3 }}>
                  <p
                    sx={{ marginBottom: 1, fontSize: "14px", fontWeight: 500 }}
                  >
                    Company Support Email Address
                  </p>
                  <Input
                    name="companySupportEmailAddress"
                    value={formData.companySupportEmailAddress || ""}
                    onChange={handleInputChange}
                    disabled={!isEditMode}
                    placeholder="e.g. support@example.com"
                    style={{
                      height: "40px",
                      fontSize: "14px",
                      padding: "8px 12px",
                    }}
                  />
                </Box>
                <Box sx={{ marginBottom: 3 }}>
                  <p
                    sx={{ marginBottom: 1, fontSize: "14px", fontWeight: 500 }}
                  >
                    Company Support Line
                  </p>
                  <Input
                    name="companySupportLine"
                    value={formData.companySupportLine || ""}
                    onChange={handleInputChange}
                    disabled={!isEditMode}
                    placeholder="e.g. +234 765 667 7889"
                    style={{
                      height: "40px",
                      fontSize: "14px",
                      padding: "8px 12px",
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </div>
        </div>
        <div style={SaveChangesBoxStyle}>
          <CustomButton
            onClick={toggleEditMode}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            loading={isLoading}
            disabled={isLoading}
          >
            {/* ✅ Dynamic button text based on whether it's create or update */}
            {isEditMode
              ? data?.id
                ? "Save Changes"
                : "Create Company"
              : "Edit"}
          </CustomButton>
        </div>
      </>
    </div>
  );
};

export default GeneralSetup;
